import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackingService, trackClick, trackReportDownload } from '../services/trackingService';
import { getUser } from '../utils/user';
import { TRACKING_CONFIG, isEventTypeEnabled } from '../config/tracking.config';

// Hook optimizado para tracking automático de page views
export const usePageTracking = (enabled: boolean = true) => {
  const location = useLocation();
  const startTimeRef = useRef<number>(Date.now());
  const lastUrlRef = useRef<string>('');

  useEffect(() => {
    if (!enabled) return;
    
    const currentUrl = location.pathname + location.search;
    
    // Solo trackear si cambió la URL (optimización para fluidez)
    if (currentUrl !== lastUrlRef.current) {
      // Calcular tiempo en la página anterior ANTES de cambiar
      let timeOnPage = 0;
      if (lastUrlRef.current) {
        timeOnPage = Math.floor((Date.now() - startTimeRef.current) / 1000);
      }
      
      // Obtener referrer persistente desde sessionStorage
      const storedReferrer = sessionStorage.getItem('tracking_previous_page');
      let referrer = 'direct';
      
      if (storedReferrer) {
        referrer = storedReferrer;
      } else if (document.referrer && document.referrer !== window.location.origin) {
        referrer = document.referrer;
      }
      
      // Guardar página actual como referrer para la próxima navegación
      sessionStorage.setItem('tracking_previous_page', window.location.href);
      
      // Actualizar referencias
      lastUrlRef.current = currentUrl;
      startTimeRef.current = Date.now(); // Resetear tiempo para nueva página
      
      // Trackear inmediatamente (no bloqueante)
      const pageUrl = window.location.href;
      const pageTitle = document.title;
      
      // Envío inmediato con tiempo real
      trackingService.trackPageView({
        page_url: pageUrl,
        page_title: pageTitle,
        referrer: referrer,
        time_on_page: timeOnPage // Tiempo real de la página anterior
      });
    }
  }, [location, enabled]);

  // Trackear tiempo al salir de la aplicación (solo para cierre de pestaña)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const timeOnPage = Math.floor((Date.now() - startTimeRef.current) / 1000);
      if (timeOnPage > 0) {
        console.log('🚪 Usuario saliendo de la app, tiempo final:', timeOnPage, 'segundos');
        
        // Envío silencioso - no bloquear navegación
        const payload = {
          user_id: trackingService.getCurrentUser()?.toString(),
          session_id: trackingService.getSessionId(),
          page_url: window.location.href,
          page_title: document.title,
          referrer: document.referrer || 'direct',
          time_on_page: timeOnPage,
          ip_address: trackingService.getBrowserInfo().ip_address,
          user_agent: trackingService.getBrowserInfo().user_agent
        };
                
        // Usar sendBeacon para envío no bloqueante
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            'http://localhost:3013/page-view/track',
            JSON.stringify(payload)
          );
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
};

// Función para limpiar el referrer persistente
export const clearTrackingReferrer = () => {
  sessionStorage.removeItem('tracking_previous_page');
  console.log('🧹 Referrer persistente limpiado');
};

// Hook para tracking de clics con throttling
export const useClickTracking = (enabled: boolean = true) => {
  const lastClickTime = useRef<number>(0);
  const CLICK_THROTTLE = TRACKING_CONFIG.CLICK_THROTTLE;

  const handleClick = useCallback((event: MouseEvent) => {
    if (!enabled) return;
    
    const now = Date.now();
    if (now - lastClickTime.current < CLICK_THROTTLE) {
      return; // Ignorar clics muy frecuentes
    }
    
    lastClickTime.current = now;
    
    const target = event.target as HTMLElement;
    
    // Solo trackear clics en elementos interactivos importantes
    if (target.tagName === 'BUTTON' || 
        target.tagName === 'A' || 
        target.closest('button') || 
        target.closest('a') ||
        target.getAttribute('data-track')) {
      
      const element = target.closest('button, a, [data-track]') as HTMLElement;
      if (element) {
        trackClick(element, window.location.href, event);
      }
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    
    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [handleClick, enabled]);
};

// Hook para tracking de descarga de reportes
export const useReportDownloadTracking = () => {
  const trackDownload = useCallback((
    studentId: string,
    reportType: string,
    fileName?: string,
    fileSize?: number,
    downloadUrl?: string
  ) => {
    trackReportDownload(studentId, reportType, fileName, fileSize, downloadUrl);
  }, []);

  return { trackDownload };
};

// Hook principal que combina todos los trackings
export const useTracking = () => {
  const user = getUser();
  
  // Solo activar tracking si hay un usuario autenticado
  useEffect(() => {
    if (user?.id) {
      // Trackear login del usuario
      trackingService.trackUserBehavior({
        action: 'user_login',
        metadata: {
          user_type: user.role || 'unknown',
          login_time: new Date().toISOString(),
        },
      });
    }
  }, [user?.id, user?.role]);

  return {
    trackDownload: useReportDownloadTracking().trackDownload,
    trackUserAction: (action: string, metadata?: any) => {
      trackingService.trackUserBehavior({
        action,
        metadata,
      });
    },
    trackCustomEvent: (eventData: any) => {
      trackingService.trackUserBehavior(eventData);
    },
  };
};

// Hook para tracking específico de estudiantes
export const useStudentTracking = (studentId?: string) => {
  const trackStudentAction = useCallback((action: string, metadata?: any) => {
    if (studentId) {
      trackingService.trackUserBehavior({
        action: `student_${action}`,
        metadata: {
          student_id: studentId,
          ...metadata,
        },
      });
    }
  }, [studentId]);

  const trackStudentReportDownload = useCallback((
    reportType: string,
    fileName?: string,
    fileSize?: number,
    downloadUrl?: string
  ) => {
    if (studentId) {
      trackReportDownload(studentId, reportType, fileName, fileSize, downloadUrl);
    }
  }, [studentId]);

  return {
    trackStudentAction,
    trackStudentReportDownload,
  };
};

// Hook para tracking de formularios
export const useFormTracking = (formName: string) => {
  const trackFormStart = useCallback(() => {
    trackingService.trackUserBehavior({
      action: 'form_start',
      metadata: { form_name: formName },
    });
  }, [formName]);

  const trackFormSubmit = useCallback((success: boolean, metadata?: any) => {
    trackingService.trackUserBehavior({
      action: success ? 'form_submit_success' : 'form_submit_error',
      metadata: {
        form_name: formName,
        ...metadata,
      },
    });
  }, [formName]);

  const trackFormField = useCallback((fieldName: string, action: string) => {
    trackingService.trackUserBehavior({
      action: 'form_field_interaction',
      metadata: {
        form_name: formName,
        field_name: fieldName,
        field_action: action,
      },
    });
  }, [formName]);

  return {
    trackFormStart,
    trackFormSubmit,
    trackFormField,
  };
};

export default useTracking;
